/**
 * File: src/store/slices/backend-process.js
 * Layer: Redux Slice
 * Description: Stores shared backend-process state used by header controls and
 * long-running workflow pages.
 * Created: 2026-08-13
 */
import { createSlice } from '@reduxjs/toolkit';

import { getMyBackendProcesses as getMyBackendProcessesAPI } from 'api/backend-processes';
import { LOGOUT } from 'store/actions';

const ACTIVE_PROCESS_STATUSES = ['queued', 'processing'];

const initialState = {
    processes: [],
    loading: false,
    initialized: false,
    error: ''
};

const getProcessTimeValue = (process = {}) => {
    const value = process?.updatedAt || process?.completedAt || process?.startedAt || process?.createdAt;
    const timestamp = value ? new Date(value).getTime() : 0;
    return Number.isFinite(timestamp) ? timestamp : 0;
};

const sortProcesses = (processes = []) =>
    [...processes].sort((left, right) => getProcessTimeValue(right) - getProcessTimeValue(left));

const shouldReplaceExistingProcess = (existingProcess = {}, incomingProcess = {}) => {
    const existingTime = getProcessTimeValue(existingProcess);
    const incomingTime = getProcessTimeValue(incomingProcess);

    if (incomingTime > existingTime) return true;
    if (incomingTime < existingTime) return false;

    // When timestamps are equal, keep the terminal or more detailed state.
    if (!existingProcess?.isActive && incomingProcess?.isActive) return false;
    if (existingProcess?.isActive && !incomingProcess?.isActive) return true;

    return Object.keys(incomingProcess || {}).length >= Object.keys(existingProcess || {}).length;
};

const mergeProcesses = ({ existingProcesses = [], incomingProcesses = [] }) => {
    const mergedById = new Map();
    const incomingProcessIds = new Set(incomingProcesses.map((process) => process?._id).filter(Boolean));

    const absorbProcess = (process) => {
        if (!process?._id) return;

        const currentProcess = mergedById.get(process._id);
        if (!currentProcess) {
            mergedById.set(process._id, process);
            return;
        }

        if (!shouldReplaceExistingProcess(currentProcess, process)) {
            return;
        }

        mergedById.set(process._id, {
            ...currentProcess,
            ...process
        });
    };

    incomingProcesses.forEach(absorbProcess);
    existingProcesses.forEach(absorbProcess);

    return sortProcesses(
        [...mergedById.values()].filter((process) => incomingProcessIds.has(process?._id) || process?.isActive)
    );
};

const slice = createSlice({
    name: 'backendProcess',
    initialState,
    reducers: {
        setLoading(state, action) {
            state.loading = action.payload;
        },
        setInitialized(state, action) {
            state.initialized = action.payload;
        },
        setError(state, action) {
            state.error = action.payload || '';
        },
        setProcesses(state, action) {
            state.processes = mergeProcesses({
                existingProcesses: state.processes,
                incomingProcesses: action.payload?.data || []
            });
        },
        upsertProcess(state, action) {
            const incomingProcess = action.payload || null;
            if (!incomingProcess?._id) return;

            const existingIndex = state.processes.findIndex((item) => item._id === incomingProcess._id);
            if (existingIndex < 0) {
                state.processes = sortProcesses([incomingProcess, ...state.processes]);
                return;
            }

            const existingProcess = state.processes[existingIndex];
            if (!shouldReplaceExistingProcess(existingProcess, incomingProcess)) {
                return;
            }

            const nextProcesses = [...state.processes];
            nextProcesses[existingIndex] = {
                ...existingProcess,
                ...incomingProcess
            };
            state.processes = sortProcesses(nextProcesses);
        },
        clearProcesses(state) {
            state.processes = [];
            state.loading = false;
            state.initialized = false;
            state.error = '';
        }
    },
    extraReducers: (builder) => {
        builder.addCase(LOGOUT, () => initialState);
    }
});

export const { upsertProcess, clearProcesses } = slice.actions;

export default slice.reducer;

export function fetchBackendProcesses(params = {}) {
    return async (dispatch) => {
        dispatch(slice.actions.setLoading(true));
        dispatch(slice.actions.setError(''));

        try {
            const response = await getMyBackendProcessesAPI(params);
            dispatch(slice.actions.setProcesses(response));
            dispatch(slice.actions.setInitialized(true));
            return response;
        } catch (err) {
            dispatch(slice.actions.setError(err?.message || 'Unable to load backend processes.'));
            throw err;
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export const selectActiveBackendProcesses = (state) =>
    (state?.backendProcess?.processes || []).filter((process) => ACTIVE_PROCESS_STATUSES.includes(process?.status));
